import type { BaselineFixture, Metric, MetricSource } from './types';

/**
 * One in-network PPO practice on Open Dental, backup Apr 30 2026, launch May 8
 * 2026 (handoff §10.2). All values illustrative but consistent across the page.
 */
export const BASELINE_FIXTURE: BaselineFixture = {
  snapshot: {
    id: 'BL-0007',
    state: 'draft',
    pms: 'Open Dental 25.4',
    network: 'in',
    backupAt: '2026-04-30T23:40:00-05:00',
    window: { from: '2025-05-01', to: '2026-04-30' },
    balancesAsOf: '2026-04-30',
    launchDate: '2026-05-08',
    practice: 'Alamo Heights Dental',
    location: 'San Antonio, TX',
    lockedAt: 'May 5, 2026',
  },
  dataStatus: {
    typesCounted: 11,
    typesTotal: 13,
    fromNotes: 1,
    inferred: 1,
    unmappedAdjustmentTypes: 2,
    unmappedAmount: 4200,
  },
  facts: {
    productionPosted: 1420000,
    procedures: 9840,
    writeOffs: { total: -444000, contract: -426000, other: -18000 },
    productionAfterWriteOffs: 976000,
    collectionsPosted: 941000,
    collectedRatio: 0.964,
    patientsSeen: 1820,
    claimsSent: { total: 3210, primary: 2890, secondary: 320, preauthsExcluded: 214 },
    whoPaid: { insurers: 612000, patients: 329000 },
    insurerPayments: [
      { payer: 'Sunlife PPO', amount: 171000 },
      { payer: 'Aetna PPO', amount: 147000 },
      { payer: 'BCBS', amount: 110000 },
      { payer: 'Cigna', amount: 73000 },
      { payer: 'Delta Dental', amount: 49000 },
      { payer: '6 others', amount: 62000, grouped: true },
    ],
  },
  balances: {
    insurers: {
      total: 46200,
      claims: 112,
      agedFrom: 'first_sent',
      buckets: [
        ['0–30 d', 21000],
        ['31–60 d', 11000],
        ['61–90 d', 7000],
        ['91–120 d', 3800],
        ['120+ d', 3400],
      ],
    },
    patients: {
      total: 18400,
      accounts: 214,
      bucketsFrom: 'pms_native',
      buckets: [
        ['0–30 d', 9200],
        ['31–60 d', 4100],
        ['61–90 d', 2300],
        ['90+ d', 2800],
      ],
    },
    creditBalances: { total: 3900, accounts: 41 },
    unappliedPayments: { total: 1150, payments: 7 },
  },
  findings: [
    { id: 'unsent', name: 'Created, never sent', rule: 'Claim created more than 3 days ago, still unsent or waiting to send', count: 38, unit: 'claims', billed: 21400, expected: 12900, basis: 'counted' },
    { id: 'on_hold', name: 'On hold or waiting for attachments', rule: 'Status on hold, or attachment required and none attached', count: 14, unit: 'claims', billed: 9800, expected: 6100, basis: 'counted' },
    { id: 'rejected_not_resent', name: 'Rejected, never sent again', rule: 'Clearinghouse or insurer rejected it before review; no later send', count: 22, unit: 'claims', billed: 14600, expected: 8700, basis: 'counted' },
    { id: 'unpaid_60', name: 'Sent, no payment after 60 days', rule: 'First sent 60+ days before Apr 30; no insurer payment posted', count: 31, unit: 'claims', billed: 24600, expected: 14200, basis: 'counted' },
    { id: 'secondary_unbilled', name: 'Secondary insurance never billed', rule: 'Primary paid, patient had an active secondary plan, no secondary claim', count: 46, unit: 'claims', billed: 8900, expected: 5300, basis: 'counted' },
    { id: 'no_claim', name: 'Completed, no claim', rule: 'Completed procedure on an insured patient, billable code, no claim attached', count: 61, unit: 'procedures', billed: 18900, expected: 11300, basis: 'counted' },
    { id: 'below_contract', name: 'Paid below your contract', rule: 'Insurer allowed amount under your contracted fee by more than $5; no later payment. $7,200 is still inside the appeal window.', count: 156, unit: 'lines', billed: null, shortfall: 9600, appealable: 7200, basis: 'counted' },
    { id: 'preauth_not_done', name: 'Pre-auth approved, treatment not done', rule: 'Pre-auth response received, procedure still planned 6+ months later. The PMS has no expiry date, so this is a rule, not a record.', count: 14, unit: 'pre-auths', billed: 27400, expected: 19900, basis: 'inferred' },
  ],
  findingsOut: {
    below_contract: {
      name: 'Paid less than this insurer usually pays',
      rule: "Out-of-network, so no contract. Line paid under 60% of this insurer's usual share for the same code; no later payment.",
      count: 88,
      shortfall: 6400,
      appealable: 6400,
    },
  },
  insurersIn: [
    { payer: 'Sunlife PPO', claimsSent: 898, paidFirstTime: 0.91, medianDays: 24, linesDenied: 0.041, paidVsContract: 0.992, topDenial: 'Frequency or maximum reached', payMethod: 'EFT' },
    { payer: 'Aetna PPO', claimsSent: 770, paidFirstTime: 0.84, medianDays: 38, linesDenied: 0.068, paidVsContract: 0.961, topDenial: 'Attachment missing', payMethod: 'Virtual card 62%' },
    { payer: 'BCBS', claimsSent: 578, paidFirstTime: 0.89, medianDays: 31, linesDenied: 0.046, paidVsContract: 0.987, topDenial: 'Other insurer pays first', payMethod: 'EFT' },
    { payer: 'Cigna', claimsSent: 385, paidFirstTime: 0.86, medianDays: 35, linesDenied: 0.052, paidVsContract: 0.974, topDenial: 'Coverage ended', payMethod: 'Check' },
    { payer: 'Delta Dental', claimsSent: 257, paidFirstTime: 0.88, medianDays: 36, linesDenied: 0.064, paidVsContract: 0.979, topDenial: 'Not a covered benefit', payMethod: 'EFT' },
    { payer: '6 others', claimsSent: 322, paidFirstTime: 0.85, medianDays: 34, linesDenied: 0.058, paidVsContract: null, topDenial: 'Mixed', payMethod: 'Mixed', grouped: true },
  ],
  insurersOut: [
    { payer: 'Delta Dental (CA)', proceduresBilled: 4020, paysAnythingOn: 0.16, shareOfFee: 0.47, medianDays: 29, topDenial: 'Paid to patient instead', payMethod: 'Check' },
    { payer: 'MetLife', proceduresBilled: 1560, paysAnythingOn: 0.8, shareOfFee: 0.62, medianDays: 22, topDenial: 'Frequency or maximum reached', payMethod: 'Virtual card 95%' },
    { payer: 'Guardian', proceduresBilled: 1300, paysAnythingOn: 0.7, shareOfFee: 0.65, medianDays: 26, topDenial: 'Not a covered benefit', payMethod: 'EFT' },
    { payer: 'Cigna', proceduresBilled: 810, paysAnythingOn: 0.82, shareOfFee: 0.61, medianDays: 31, topDenial: 'Coverage ended', payMethod: 'Virtual card 90%' },
  ],
  payments: {
    virtualCards: { count: 1040, amount: 171000, share: 0.28, estFees: 5100 },
    autoPostedShare: 0.41,
    paidToPostedMedianDays: 6,
    paymentsMeasured: 1380,
  },
  denials: {
    total: 236,
    fromCodes: 198,
    fromNotes: 38,
    reasons: [
      { reason: 'Attachment missing', count: 52, catchable: 'before_sending' },
      { reason: 'Not a covered benefit', count: 41, catchable: 'before_visit' },
      { reason: 'Frequency or maximum reached', count: 38, catchable: 'before_visit' },
      { reason: 'Coverage ended', count: 33, catchable: 'before_visit' },
      { reason: 'Other insurer pays first', count: 29, catchable: 'before_sending' },
      { reason: 'Other reasons', count: 43, catchable: null },
    ],
    clearinghouseRejections: {
      claims: 61,
      shareOfSent: 0.019,
      top: [
        ['Subscriber ID mismatch', 24],
        ['Provider NPI missing', 11],
        ['Invalid date of birth', 9],
      ],
    },
  },
  value: {
    locked: false,
    oneTime: [
      { findingId: 'unsent', expected: 12900, share: 0.8, estimate: 10300 },
      { findingId: 'on_hold', expected: 6100, share: 0.8, estimate: 4900 },
      { findingId: 'rejected_not_resent', expected: 8700, share: 0.8, estimate: 7000 },
      { findingId: 'unpaid_60', expected: 14200, share: 0.7, estimate: 9900 },
      { findingId: 'secondary_unbilled', expected: 5300, share: 0.85, estimate: 4500 },
      { findingId: 'no_claim', expected: 11300, share: 0.6, estimate: 6800 },
      { findingId: 'below_contract', expected: 7200, share: 0.5, estimate: 3600 },
      { findingId: 'preauth_not_done', expected: 19900, share: 0.25, estimate: 5000 },
    ],
    oneTimeTotalDeduped: 52000,
    yearly: { catchableDenials: 193, expected: 21300, share: 0.6, estimate: 12800 },
    hours: {
      staffCostPerHour: 32,
      workingDays: 249,
      tasks: [
        { task: 'Creating a claim, with x-rays and narrative', perDay: 13, basis: 'counted', minutes: 6, elvaShare: 0.85 },
        { task: 'Posting an insurer payment by hand', perDay: 9, basis: 'counted', minutes: 5, elvaShare: 0.7 },
        { task: 'Following up a claim with the insurer', perDay: 6, basis: 'from_notes', minutes: 15, elvaShare: 0.7 },
        { task: 'Running a virtual card and posting it', perDay: 4, basis: 'counted', minutes: 6, elvaShare: 0.9 },
        { task: 'Checking eligibility and typing benefits into the chart', perDay: 11, basis: 'from_notes', minutes: 12, elvaShare: 0.95 },
      ],
      byHandPerDay: 6.2,
      backPerDay: 5.1,
      backPerYearUsd: 40900,
    },
  },
  day1: [
    { id: 'send_unsent', label: 'Review and send unsent claims', items: 38, destination: 'claims/drafts', status: 'needs_approval', sub: 'Checks codes, attachments and narrative first' },
    { id: 'release_held', label: 'Attach documents and release held claims', items: 14, destination: 'claims/drafts', status: 'needs_approval', note: 'Found 9 attachments in imaging; 5 need your team', sub: 'Found 9 attachments in imaging; 5 need your team' },
    { id: 'resend_rejected', label: 'Correct and resend rejected claims', items: 22, destination: 'claims/rejected', status: 'needs_approval' },
    { id: 'status_calls', label: 'Call insurers on claims unpaid after 60 days', items: 31, destination: 'ai-queue', status: 'needs_approval', sub: 'Spread over 4 days inside your calling hours' },
    { id: 'bill_secondary', label: 'Bill secondary insurance', items: 46, destination: 'claims/drafts', status: 'needs_approval' },
    { id: 'check_no_claim', label: 'Check completed procedures with no claim', items: 61, destination: 'worklist', status: 'needs_approval', sub: 'Some may be intentional (no-charge, bundled)' },
    { id: 'draft_appeals', label: 'Draft appeals for underpaid claims', items: 31, destination: 'claims/appeals', status: 'needs_approval', sub: 'Only those still inside the appeal window' },
    { id: 'confirm_preauth', label: 'Confirm treatment with pre-auth patients', items: 14, destination: 'worklist', status: 'needs_approval' },
  ],
};

/* ── Drawer metrics (handoff §5.8) ────────────────────────────────────────
 * Every number opens the drawer. The registry below carries the rich content;
 * numbers without a registered entry get a synthesized generic metric from
 * `getMetric`, so the drawer always opens with a definition + exact source. */

const WINDOW = BASELINE_FIXTURE.snapshot.window;
const SNAP = BASELINE_FIXTURE.snapshot.id;

function src(tables: string[], rule: string, amountField?: string): MetricSource {
  return {
    pms: 'Open Dental 25.4',
    tables,
    rule,
    ...(amountField !== undefined ? { amountField } : {}),
    calcVersion: 'baseline-1.0',
    snapshotId: SNAP,
  };
}

export const BASELINE_METRICS: Record<string, Metric> = {
  'facts.production_posted': {
    id: 'facts.production_posted', label: 'Production posted', value: 1420000, unit: 'usd', basis: 'counted', layer: 'Facts', window: WINDOW,
    definition: 'Completed procedures in the 12-month window, valued at the fee posted in the PMS. Not a collected figure.',
    reconciliation: { report: 'Open Dental Production report', reportValue: 1423100, difference: 0.0022, status: 'matches' },
    source: src(['procedurelog'], 'ProcStatus = C · ProcDate in window', 'sum of procedurelog.ProcFee'),
  },
  'facts.write_offs': {
    id: 'facts.write_offs', label: 'Write-offs & adjustments', value: -444000, unit: 'usd', basis: 'counted', layer: 'Facts', window: WINDOW,
    definition: 'Adjustments posted against production in the window: insurance contract write-offs and other adjustments, shown apart.',
    tiles: [{ label: 'Contract', value: -426000, unit: 'usd' }, { label: 'Other', value: -18000, unit: 'usd' }],
    source: src(['adjustment'], 'AdjAmt < 0 · AdjDate in window · grouped by AdjType'),
  },
  'facts.production_after': {
    id: 'facts.production_after', label: 'Production after write-offs', value: 976000, unit: 'usd', basis: 'counted', layer: 'Facts', window: WINDOW,
    definition: 'Production posted less write-offs and adjustments. What you could expect to collect.',
    source: src(['procedurelog', 'adjustment'], 'Production posted − write-offs & adjustments'),
  },
  'facts.collections_posted': {
    id: 'facts.collections_posted', label: 'Collections posted', value: 941000, unit: 'usd', basis: 'counted', layer: 'Facts', window: WINDOW,
    definition: 'Insurer and patient payments posted in the window, less refunds. Includes payments for work done earlier.',
    reconciliation: { report: 'Open Dental Collections report', reportValue: 942800, difference: 0.0019, status: 'matches' },
    source: src(['payment', 'claimpayment'], 'Payment + ClaimPayment in window, less refunds'),
  },
  'facts.collected_ratio': {
    id: 'facts.collected_ratio', label: 'Collected ÷ production after write-offs', value: 0.964, unit: 'pct', basis: 'counted', layer: 'Facts', window: WINDOW,
    definition: 'Collections posted divided by production after write-offs, over the same 12 months. Includes payments for earlier work, so it is not a pure in-period rate.',
    source: src(['payment', 'procedurelog', 'adjustment'], 'Collections posted ÷ production after write-offs'),
  },
  'facts.patients_seen': {
    id: 'facts.patients_seen', label: 'Patients seen', value: 1820, unit: 'count', basis: 'counted', layer: 'Facts', window: WINDOW,
    definition: 'Patients with at least one completed visit in the 12 months. Not the full active-patient list.',
    source: src(['procedurelog', 'patient'], 'distinct PatNum with a completed procedure in window'),
  },
  'facts.claims_sent': {
    id: 'facts.claims_sent', label: 'Claims sent', value: 3210, unit: 'count', basis: 'counted', layer: 'Facts', window: WINDOW,
    definition: 'Primary and secondary claims sent in the window. Pre-authorizations are counted apart (214) and not included here.',
    tiles: [{ label: 'Primary', value: 2890, unit: 'count' }, { label: 'Secondary', value: 320, unit: 'count' }, { label: 'Pre-auths', value: 214, unit: 'count' }],
    source: src(['claim'], 'ClaimType in (P, S) · DateSent in window'),
  },
  'facts.who_paid': {
    id: 'facts.who_paid', label: 'Who paid the $941,000', value: 941000, unit: 'usd', basis: 'counted', layer: 'Facts', window: WINDOW,
    definition: 'The $941,000 collected, split by who paid it: insurers vs patients. Payer mix elsewhere on the page uses insurer payments only.',
    tiles: [{ label: 'Insurers', value: 612000, unit: 'usd' }, { label: 'Patients', value: 329000, unit: 'usd' }],
    source: src(['payment', 'claimpayment'], 'Collections split by payment source'),
  },
  'facts.insurer_payments': {
    id: 'facts.insurer_payments', label: 'Insurer payments by insurer', value: 612000, unit: 'usd', basis: 'counted', layer: 'Facts', window: WINDOW,
    definition: 'Insurer payments posted in the window, grouped by payer ID (not by the name typed in the PMS). The top 5 are shown; the rest grouped.',
    source: src(['claimpayment', 'insplan', 'carrier'], 'ClaimPayment grouped by payer ID'),
  },
  'balances.owed_insurers': {
    id: 'balances.owed_insurers', label: 'Owed by insurers on sent claims', value: 46200, unit: 'usd', basis: 'counted', layer: 'Facts', asOf: BASELINE_FIXTURE.snapshot.balancesAsOf,
    definition: 'Insurer estimate still unpaid on claims sent, aged from the date each claim was first sent. Not relabelled or netted against credits.',
    tiles: [{ label: 'Claims', value: 112, unit: 'count' }, { label: 'Owed', value: 46200, unit: 'usd' }],
    reconciliation: { report: 'Open Dental Insurance Aging report', reportValue: 46200, difference: 0, status: 'matches' },
    source: src(['claim', 'claimproc'], 'Sent claims with InsPayEst > InsPayAmt, aged from first DateSent', 'sum of unpaid InsPayEst'),
  },
  'balances.owed_patients': {
    id: 'balances.owed_patients', label: 'Owed by patients', value: 18400, unit: 'usd', basis: 'counted', layer: 'Facts', asOf: BASELINE_FIXTURE.snapshot.balancesAsOf,
    definition: "Patient balances in Open Dental's own aging buckets, exactly as the PMS reports them. Never relabelled.",
    tiles: [{ label: 'Accounts', value: 214, unit: 'count' }, { label: 'Owed', value: 18400, unit: 'usd' }],
    reconciliation: { report: 'Open Dental Aging of A/R report', reportValue: 18400, difference: 0, status: 'matches' },
    source: src(['patient', 'paysplit'], "Patient portion of the aged balance, PMS native buckets"),
  },
  'balances.credit_balances': {
    id: 'balances.credit_balances', label: 'Credit balances', value: 3900, unit: 'usd', basis: 'counted', layer: 'Facts', asOf: BASELINE_FIXTURE.snapshot.balancesAsOf,
    definition: 'Accounts in credit — may need a refund or to be applied. Shown on its own, never subtracted from what is owed.',
    tiles: [{ label: 'Accounts', value: 41, unit: 'count' }, { label: 'Credit', value: 3900, unit: 'usd' }],
    source: src(['patient'], 'EstBalance < 0 on the balances date'),
  },
  'balances.unapplied': {
    id: 'balances.unapplied', label: 'Payments not applied to any visit', value: 1150, unit: 'usd', basis: 'counted', layer: 'Facts', asOf: BASELINE_FIXTURE.snapshot.balancesAsOf,
    definition: 'Payments posted but not allocated to a procedure. Shown on its own, never subtracted from what is owed.',
    tiles: [{ label: 'Payments', value: 7, unit: 'count' }, { label: 'Unapplied', value: 1150, unit: 'usd' }],
    source: src(['payment', 'paysplit'], 'Payment amount with no matching procedure allocation'),
  },

  // Findings — the drawer the wireframe shows in full is "unsent".
  'finding.unsent': {
    id: 'finding.unsent', label: 'Created, never sent', value: 38, unit: 'count', basis: 'counted', layer: 'Findings', window: WINDOW,
    definition: 'Claims your PMS shows as created but never sent, created more than 3 days before the backup. Pre-authorizations and voided claims are left out. "Expected from insurer" is the insurer estimate stored on each claim line.',
    tiles: [{ label: 'Claims', value: 38, unit: 'count' }, { label: 'Billed', value: 21400, unit: 'usd' }, { label: 'Expected from insurer', value: 12900, unit: 'usd' }],
    derivation: [
      { label: 'Unsent claims in the backup', count: 41 },
      { label: '− created in the last 3 days', count: -2 },
      { label: '− voided', count: -1 },
      { label: 'Counted', count: 38 },
    ],
    reconciliation: { report: 'Open Dental "Claims Not Sent" report', reportValue: 21400, difference: 0, status: 'matches' },
    source: src(['claim', 'claimproc'], 'ClaimStatus in (U, W) · ClaimType in (P, S) · DateSent empty · created ≤ Apr 27, 2026', 'sum of claimproc.InsPayEst per claim'),
    records: {
      total: 38, unit: 'count', more: 32,
      shown: [
        { ref: '#3981', initials: 'R. H.', insurer: 'Aetna PPO', service: 'Nov 12, 2025', billed: 1180, expected: 640 },
        { ref: '#4022', initials: 'M. T.', insurer: 'Cigna', service: 'Dec 3, 2025', billed: 285, expected: 171 },
        { ref: '#4107', initials: 'J. P.', insurer: 'BCBS', service: 'Jan 22, 2026', billed: 1520, expected: 812 },
        { ref: '#4190', initials: 'A. K.', insurer: 'Sunlife PPO', service: 'Feb 18, 2026', billed: 198, expected: 158 },
        { ref: '#4233', initials: 'S. L.', insurer: 'Delta Dental', service: 'Mar 4, 2026', billed: 940, expected: 470 },
        { ref: '#4301', initials: 'D. W.', insurer: 'Aetna PPO', service: 'Mar 29, 2026', billed: 312, expected: 218 },
      ],
    },
    day1ActionId: 'send_unsent',
  },

  'value.one_time': {
    id: 'value.one_time', label: 'One time · work ELVA inherits', value: 52000, unit: 'usd', basis: 'projected', layer: 'ELVA value',
    definition: 'Expected insurer payments on claims that are stuck today, summed across the findings with a recovery share applied to each. A claim that appears in two findings is counted once.',
    source: src(['claim', 'claimproc'], 'Σ(expected × agreed recovery share), de-duplicated across findings'),
  },
  'value.yearly': {
    id: 'value.yearly', label: 'Every year · denials caught before sending', value: 12800, unit: 'usd', basis: 'projected', layer: 'ELVA value', window: WINDOW,
    definition: "Last year's catchable denials (193), valued and multiplied by the share ELVA is expected to prevent. A yearly figure, never added to the one-time figure.",
    source: src(['claimproc', 'eob'], 'catchable denial lines × expected value × prevention share'),
  },
  'value.hours': {
    id: 'value.hours', label: 'Every working day · billing time back', value: 5.1, unit: 'hours', basis: 'projected', layer: 'ELVA value',
    definition: 'Daily billing time ELVA is expected to hand back, from task frequency (counted) and the minutes and shares agreed at kickoff. Worth $40,900 a year at $32 an hour over 249 days.',
    source: src(['procedurelog', 'claimpayment'], 'Σ(task/day × minutes × ELVA share) ÷ 60'),
  },
};

/** Synthesize a drawer metric for a finding the registry doesn't cover richly. */
function findingMetric(id: string): Metric | null {
  const key = id.replace(/^finding\./, '');
  const fd = BASELINE_FIXTURE.findings.find((x) => x.id === key);
  if (!fd) return null;
  const tiles: Metric['tiles'] = [{ label: `${fd.unit.charAt(0).toUpperCase()}${fd.unit.slice(1)}`, value: fd.count, unit: 'count' }];
  if (fd.billed != null) tiles.push({ label: 'Billed', value: fd.billed, unit: 'usd' });
  if (fd.expected != null) tiles.push({ label: 'Expected from insurer', value: fd.expected, unit: 'usd' });
  if (fd.shortfall != null) tiles.push({ label: 'Shortfall', value: fd.shortfall, unit: 'usd' });
  if (fd.appealable != null) tiles.push({ label: 'Still appealable', value: fd.appealable, unit: 'usd' });
  const day1 = BASELINE_FIXTURE.day1.find((a) => a.items === fd.count);
  return {
    id,
    label: fd.name,
    value: fd.count,
    unit: 'count',
    basis: fd.basis,
    layer: 'Findings',
    window: WINDOW,
    definition: `${fd.rule}. Money shown is the expected insurer payment, not the billed amount.`,
    tiles,
    ...(fd.basis === 'counted'
      ? { reconciliation: { report: `Open Dental work-list for "${fd.name}"`, reportValue: fd.count, difference: 0, status: 'matches' as const } }
      : {}),
    source: src(['claim', 'claimproc'], fd.rule, fd.expected != null ? 'sum of claimproc.InsPayEst per claim' : undefined),
    ...(day1 ? { day1ActionId: day1.id } : {}),
  };
}

/** The drawer content for any id — registered metric, finding, or generic fallback. */
export function getBaselineMetric(id: string): Metric {
  const m = BASELINE_METRICS[id];
  if (m) return m;
  if (id.startsWith('finding.')) {
    const fm = findingMetric(id);
    if (fm) return fm;
  }
  return {
    id,
    label: id,
    value: null,
    unit: 'count',
    basis: 'counted',
    layer: 'Facts',
    definition: 'Computed from this practice’s PMS records for the baseline window.',
    source: src(['(see Data & definitions)'], 'See Data & definitions for the full rule.'),
  };
}
